import { auth, db, firebaseImport, FirebaseTimestamp } from '../../config/main_config';
import { createToast } from './createToast.util';
import { closeModal } from './closeModal.util';
import { waitForCompanyID } from './waitForCompanyID.util';
import { openModal } from './openModal.util';
import { sanitise } from './sanitise.util';
import Tableordion from './tableordion.util';

/**
 * Interface representing a Special Interest Group.
 */
interface SpecialInterestGroup {
    id: string;
    groupName: string;
    primaryContact: string;
    contactEmail: string;
    contactPhone: string;
    internalOwner: string;
    lastUpdated: firebaseImport.firestore.Timestamp;
    comments?: string;
    addedBy: string;
    dateAdded: firebaseImport.firestore.Timestamp;
    updatedBy: string;
}

/**
 * Renders the special interest groups into a table.
 * 
 * @param groups - Array of SpecialInterestGroup objects.
 */
export async function renderSpecialInterestGroupsTable(groups: SpecialInterestGroup[]): Promise<void> {
    const tableContainer = document.querySelector('#specialInterestGroupsTable .tableordion-body');

    if (!tableContainer) {
        console.error('Special Interest Groups table container not found.');
        return;
    }

    let html = '';

    // Sort the groups by ID
    groups.sort((a, b) => parseInt(a.id) - parseInt(b.id));

    groups.forEach(group => {
        html += `
            <tr data-id="${group.id}" class="tableordion-row">
                <td>${group.id}</td>
                <td>${sanitise(group.groupName)}</td>
                <td>${sanitise(group.primaryContact)}</td>
                <td>${sanitise(group.contactEmail)}</td>
                <td>${sanitise(group.contactPhone)}</td>
                <td>${sanitise(group.internalOwner)}</td>
                <td>${group.lastUpdated.toDate().toLocaleDateString()}</td>
                <td>
                    <button class="action-btn edit-group-btn" data-id="${group.id}">
                        <i class="fa-duotone fa-solid fa-pencil edit-icon" alt="Edit"></i>
                    </button>
                    <button class="action-btn delete-group-btn" data-id="${group.id}">
                        <i class="fa-duotone fa-trash delete-icon color-red" alt="Delete"></i>
                    </button>
                </td>
            </tr>
        `;
    });

    tableContainer.innerHTML = html;

    const accordions = new Tableordion(".tableordion-row");

    // Attach event listeners to edit and delete buttons
    attachGroupButtonsListeners();
}

/**
 * Attaches event listeners to all edit and delete buttons in the table.
 */
function attachGroupButtonsListeners(): void {
    const editButtons = document.querySelectorAll('.edit-group-btn');
    const deleteButtons = document.querySelectorAll('.delete-group-btn');

    editButtons.forEach(button => {
        button.addEventListener('click', async (event) => {
            const target = event.currentTarget as HTMLElement;
            const groupId = target.getAttribute('data-id');

            if (!groupId) {
                console.error('Group ID not found on the edit button.');
                return;
            }

            const groups = (window as any).specialInterestGroups as SpecialInterestGroup[];
            const group = groups.find(grp => grp.id === groupId);

            if (!group) {
                console.error('Group not found for the given ID.');
                return;
            }

            openEditGroupModal(group);
        });
    });

    deleteButtons.forEach(button => {
        button.addEventListener('click', async (event) => {
            const target = event.currentTarget as HTMLElement;
            const groupId = target.getAttribute('data-id');

            if (!groupId) {
                console.error('Group ID not found on the delete button.');
                return;
            }

            if (confirm('Are you sure you want to delete this group?')) {
                await deleteGroup(groupId);
            }
        });
    });
}

/**
 * Opens the edit group modal and populates it with current data.
 * 
 * @param group - The SpecialInterestGroup object to edit.
 */
export function openEditGroupModal(group: SpecialInterestGroup): void {
    const modal = document.querySelector('[data-managedid="EditGroupModal"]') as HTMLElement;
    if (!modal) {
        console.error('Edit Group Modal not found.');
        return;
    }

    // Populate the modal fields
    const groupIdElement = modal.querySelector('#groupId_edit') as HTMLInputElement;
    const groupNameElement = modal.querySelector('#groupName_edit') as HTMLInputElement;
    const primaryContactElement = modal.querySelector('#primaryContact_edit') as HTMLInputElement;
    const contactEmailElement = modal.querySelector('#contactEmail_edit') as HTMLInputElement;
    const contactPhoneElement = modal.querySelector('#contactPhone_edit') as HTMLInputElement;
    const internalOwnerElement = modal.querySelector('#internalOwner_edit') as HTMLInputElement;
    const commentsElement = modal.querySelector('#comments_edit') as HTMLTextAreaElement;

    const missingElements = [];
    if (!groupIdElement) missingElements.push('Group ID');
    if (!groupNameElement) missingElements.push('Group Name');
    if (!primaryContactElement) missingElements.push('Primary Contact');
    if (!contactEmailElement) missingElements.push('Contact Email');
    if (!contactPhoneElement) missingElements.push('Contact Phone');
    if (!internalOwnerElement) missingElements.push('Internal Owner');

    if (missingElements.length > 0) {
        console.error('Missing elements:', missingElements);
        return;
    }

    modal.dataset.id = group.id;
    groupIdElement.value = group.id;
    groupNameElement.value = group.groupName;
    primaryContactElement.value = group.primaryContact;
    contactEmailElement.value = group.contactEmail;
    contactPhoneElement.value = group.contactPhone;
    internalOwnerElement.value = group.internalOwner;
    commentsElement.value = group.comments || '';

    // Open the modal
    openModal('[data-managedid="EditGroupModal"]');
}

/**
 * Adds a new special interest group to Firestore.
 */
export async function addGroup(
    groupName: string,
    primaryContact: string,
    contactEmail: string,
    contactPhone: string,
    internalOwner: string,
    comments?: string
): Promise<void> {
    const companyID = await waitForCompanyID();
    const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsSIG`;
    const docRef = db.doc(docPath);

    try {
        const doc = await docRef.get();
        let specialInterestGroups = doc.data()?.groups || {};

        // Generate a new ID
        const newId = generateNewId(specialInterestGroups);

        // Create the new group object
        const newGroup = {
            id: newId,
            groupName,
            primaryContact,
            contactEmail,
            contactPhone,
            internalOwner,
            comments,
            addedBy: auth.currentUser.uid,
            dateAdded: firebaseImport.firestore.Timestamp.now(),
            lastUpdated: firebaseImport.firestore.Timestamp.now(),
            updatedBy: auth.currentUser.uid
        };

        // Add the new group to the existing map
        specialInterestGroups[newId] = newGroup;

        // Update Firestore
        await docRef.update({ groups: specialInterestGroups });

        createToast({
            message: 'Group added successfully.',
            customStyles: {
                backgroundColor: '#28a745',
                color: '#ffffff'
            }
        });

        closeModal(document.querySelector('[data-managedid="AddGroupModal"]'));

    } catch (error) {
        console.error('Error adding group:', error);
        createToast({
            message: 'Failed to add group.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
    }
}

/**
 * Edits an existing special interest group in Firestore.
 */
export async function editGroup(
    id: string,
    groupName: string,
    primaryContact: string,
    contactEmail: string,
    contactPhone: string,
    internalOwner: string,
    comments?: string
): Promise<void> {
    const companyID = await waitForCompanyID();
    const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsSIG`;
    const docRef = db.doc(docPath);

    try {
        const doc = await docRef.get();
        let specialInterestGroups = doc.data()?.groups;

        if (!specialInterestGroups || !specialInterestGroups[id]) {
            throw new Error('Group not found');
        }

        // Update the group
        specialInterestGroups[id] = {
            ...specialInterestGroups[id],
            groupName,
            primaryContact,
            contactEmail,
            contactPhone,
            internalOwner,
            comments,
            lastUpdated: firebaseImport.firestore.Timestamp.now(),
            updatedBy: auth.currentUser.uid
        };

        // Update Firestore
        await docRef.update({ groups: specialInterestGroups });

        createToast({
            message: 'Group updated successfully.',
            customStyles: {
                backgroundColor: '#28a745',
                color: '#ffffff'
            }
        });

        closeModal(document.querySelector('[data-managedid="EditGroupModal"]'));

    } catch (error) {
        console.error('Error editing group:', error);
        createToast({
            message: 'Failed to edit group.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
    }
}

/**
 * Deletes a special interest group from Firestore.
 * 
 * @param id - The ID of the group to delete.
 */
export async function deleteGroup(id: string): Promise<void> {
    const companyID = await waitForCompanyID();
    const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsSIG`;
    const docRef = db.doc(docPath);

    try {
        await docRef.update({
            [`groups.${id}`]: firebaseImport.firestore.FieldValue.delete()
        });

        createToast({
            message: 'Group deleted successfully.',
            customStyles: {
                backgroundColor: '#28a745',
                color: '#ffffff'
            }
        });

    } catch (error) {
        console.error('Error deleting group:', error);
        createToast({
            message: 'Failed to delete group.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
    }
}

/**
 * Sets up a real-time listener for special interest groups using onSnapshot.
 */
export function setupSpecialInterestGroupsListener(): void {
    waitForCompanyID().then(companyID => {
        const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsSIG`;
        const docRef = db.doc(docPath);

        docRef.onSnapshot(docSnapshot => {
            if (docSnapshot.exists) {
                const data = docSnapshot.data()?.groups;
                if (data && typeof data === 'object') {
                    const groups: SpecialInterestGroup[] = Object.entries(data).map(([id, group]: [string, any]) => ({
                        id,
                        groupName: group.groupName,
                        primaryContact: group.primaryContact,
                        contactEmail: group.contactEmail,
                        contactPhone: group.contactPhone,
                        internalOwner: group.internalOwner,
                        comments: group.comments,
                        addedBy: group.addedBy,
                        dateAdded: group.dateAdded,
                        lastUpdated: group.lastUpdated,
                        updatedBy: group.updatedBy
                    }));
                    (window as any).specialInterestGroups = groups;
                    renderSpecialInterestGroupsTable(groups);
                }
            }
        }, error => {
            console.error('Error with onSnapshot listener:', error);
            createToast({
                message: 'Real-time updates failed.',
                customStyles: {
                    backgroundColor: '#ff0000',
                    color: '#ffffff'
                }
            });
        });
    });
}

/**
 * Generates a new unique ID for a group.
 * 
 * @param specialInterestGroups - The current groups map.
 * @returns A new unique ID as a string.
 */
function generateNewId(specialInterestGroups: { [key: string]: any }): string {
    const existingIds = Object.keys(specialInterestGroups)
        .map(id => parseInt(id, 10))
        .filter(id => !isNaN(id));
    const maxId = existingIds.length > 0 ? Math.max(...existingIds) : 0;
    return (maxId + 1).toString();
}

/**
 * Handles the submission of the Add Group form.
 */
export async function handleAddGroup(event: Event): Promise<void> {
    event.preventDefault();

    const groupNameElement = document.querySelector('#groupName_add') as HTMLInputElement;
    const primaryContactElement = document.querySelector('#primaryContact_add') as HTMLInputElement;
    const contactEmailElement = document.querySelector('#contactEmail_add') as HTMLInputElement;
    const contactPhoneElement = document.querySelector('#contactPhone_add') as HTMLInputElement;
    const internalOwnerElement = document.querySelector('#internalOwner_add') as HTMLInputElement;
    const commentsElement = document.querySelector('#comments_add') as HTMLTextAreaElement;

    if (!groupNameElement || !primaryContactElement || !contactEmailElement ||
        !contactPhoneElement || !internalOwnerElement || !commentsElement) {
        console.error('One or more form elements not found.');
        return;
    }

    // Validate required fields
    if (!groupNameElement.value || !primaryContactElement.value ||
        !contactEmailElement.value || !contactPhoneElement.value ||
        !internalOwnerElement.value) {
        createToast({
            message: 'Please fill in all required fields.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
        return;
    }

    await addGroup(
        groupNameElement.value,
        primaryContactElement.value,
        contactEmailElement.value,
        contactPhoneElement.value,
        internalOwnerElement.value,
        commentsElement.value
    );

    // Reset form
    groupNameElement.value = '';
    primaryContactElement.value = '';
    contactEmailElement.value = '';
    contactPhoneElement.value = '';
    internalOwnerElement.value = '';
    commentsElement.value = '';
}

/**
 * Handles the submission of the Edit Group form.
 */
export async function handleEditGroup(event: Event): Promise<void> {
    event.preventDefault();

    const modal = document.querySelector('[data-managedid="EditGroupModal"]') as HTMLElement;
    if (!modal) {
        console.error('Edit Group Modal not found.');
        return;
    }

    const groupId = modal.dataset.id;
    if (!groupId) {
        console.error('Group ID not found in modal dataset.');
        return;
    }

    const groupNameElement = document.querySelector('#groupName_edit') as HTMLInputElement;
    const primaryContactElement = document.querySelector('#primaryContact_edit') as HTMLInputElement;
    const contactEmailElement = document.querySelector('#contactEmail_edit') as HTMLInputElement;
    const contactPhoneElement = document.querySelector('#contactPhone_edit') as HTMLInputElement;
    const internalOwnerElement = document.querySelector('#internalOwner_edit') as HTMLInputElement;
    const commentsElement = document.querySelector('#comments_edit') as HTMLTextAreaElement;

    const missingElements = [];
    if (!groupNameElement) missingElements.push('Group Name');
    if (!primaryContactElement) missingElements.push('Primary Contact');
    if (!contactEmailElement) missingElements.push('Contact Email');
    if (!contactPhoneElement) missingElements.push('Contact Phone');
    if (!internalOwnerElement) missingElements.push('Internal Owner');

    if (missingElements.length > 0) {
        console.log('Missing elements:', missingElements);
        return;
    }

    // Validate required fields
    if (!groupNameElement.value || !primaryContactElement.value ||
        !contactEmailElement.value || !contactPhoneElement.value ||
        !internalOwnerElement.value) {
        createToast({
            message: 'Please fill in all required fields.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
        return;
    }

    await editGroup(
        groupId,
        groupNameElement.value,
        primaryContactElement.value,
        contactEmailElement.value,
        contactPhoneElement.value,
        internalOwnerElement.value,
        commentsElement.value
    );
}